/* @jsxImportSource @emotion/react */

import React from 'react'
import { css } from '@emotion/react'
import { FormattedMessage } from 'react-intl'

import { PlayIcon } from '@slite/icons'

import { Button } from '../../../components/Button/Button'
import { Section } from '../../../components/Section'
import { WebsiteText } from '../../../components/WebsiteText'
import { useRoutes } from '../../../routes'
import { UseCaseVideoModal } from './UseCaseVideoModal'

type SectionUseCaseHeroProps = {
  title: string
  description: string
  // mp4 played muted + looping behind the hero copy.
  videoSnippet: string
  // YouTube id — used to derive the poster shown before the snippet loads.
  videoLink: string
  // Attached to the hero root so the page can observe when it scrolls past
  // (to toggle the transparent/inverted navbar).
  heroRef?: React.Ref<HTMLDivElement>
}

const heroCSS = css`
  position: relative;
  overflow: hidden;
  min-height: 88vh;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`

// Cover the hero regardless of the video's aspect ratio.
const videoCSS = css`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  min-width: 100%;
  min-height: 100%;
  object-fit: cover;
  z-index: 0;
`

// Edge gradients instead of a flat dark overlay: the copy at the bottom and
// the inverted navbar at the top stay legible while the people mid-frame
// remain visible.
const overlayCSS = css`
  position: absolute;
  inset: 0;
  background:
    linear-gradient(to bottom, rgba(0, 0, 0, 0.35), transparent 25%),
    linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent 55%);
  z-index: 1;
  pointer-events: none;
`

const watchVideoButtonCSS = css`
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }
`

const centeredOnVideoCSS = css`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
`

const contentCSS = css`
  position: relative;
  z-index: 2;
  width: 100%;
`

function WatchVideoButton({
  onClick,
  className,
}: {
  onClick: () => void
  className?: string
}) {
  return (
    <button
      css={watchVideoButtonCSS}
      className={`padding-vertical-12 padding-right-20 padding-left-16 flexbox flexbox-align-center border-none radius-largest flexbox-column-gap-12 pointer shadow-large-on-hover transition padding-vertical-8-small padding-right-16-small padding-left-12-small ${className ?? ''}`}
      onClick={onClick}
    >
      <div
        className="background-white flexbox flexbox-centered radius-round"
        css={css`
          width: 32px;
          height: 32px;
        `}
      >
        <PlayIcon size={12} className="margin-left-4" />
      </div>
      <span className="font-weight-semibold white" style={{ fontSize: 17 }}>
        <FormattedMessage
          id="solutions.usecase.hero.watchVideo"
          defaultMessage="Watch video"
        />
      </span>
    </button>
  )
}

// Full-bleed hero: a muted, autoplaying, looping video fills the section with
// gradient overlays; the copy sits bottom-left with the CTA bottom-right so
// the footage stays visible. The watch-video button floats centered on the
// video, except on mobile where it joins the CTA row to stay clear of the copy.
export function SectionUseCaseHero({
  title,
  description,
  videoSnippet,
  videoLink,
  heroRef,
}: SectionUseCaseHeroProps) {
  const routes = useRoutes()
  const [isVideoOpen, setIsVideoOpen] = React.useState(false)
  const posterUrl = `https://img.youtube.com/vi/${videoLink}/maxresdefault.jpg`

  return (
    <div ref={heroRef} css={heroCSS} className="dark-mode shade-05">
      <UseCaseVideoModal
        isOpen={isVideoOpen}
        setIsOpen={setIsVideoOpen}
        videoLink={videoLink}
      />
      <video
        css={videoCSS}
        autoPlay
        muted
        loop
        playsInline
        poster={posterUrl}
        src={videoSnippet}
      />
      <div css={overlayCSS} />
      <WatchVideoButton
        css={centeredOnVideoCSS}
        className="display-none-small"
        onClick={() => setIsVideoOpen(true)}
      />
      <div css={contentCSS}>
        <Section containerVerticalPadding={64}>
          <div className="flexbox flexbox-space-between flexbox-align-end flexbox-column-gap-40 flexbox-column-small flexbox-align-start-small flexbox-row-gap-24-small">
            <div>
              <WebsiteText
                variant="title"
                htmlTag="h1"
                css={css`
                  max-width: 560px;
                `}
              >
                {title}
              </WebsiteText>
              <WebsiteText
                variant="body-lg"
                className="margin-top-16"
                css={css`
                  max-width: 620px;
                  color: rgba(255, 255, 255, 0.85);
                `}
              >
                {description}
              </WebsiteText>
            </div>
            <div className="flexbox flexbox-align-center flexbox-wrap flexbox-gap-12 flexbox-no-shrink">
              <Button
                variant="primary-brand"
                size="huge"
                href={routes.bookDemo()}
              >
                <FormattedMessage
                  id="solutions.usecase.hero.cta"
                  defaultMessage="Book demo"
                />
              </Button>
              <WatchVideoButton
                className="display-none-not-small"
                onClick={() => setIsVideoOpen(true)}
              />
            </div>
          </div>
        </Section>
      </div>
    </div>
  )
}
